import {
    backgroundRemovalImageUpload,
    getUploadedImageContent,
    resetProgressElementAttachment,
} from '../../../attachments/attachmentActions';
import { getBackgroundRemovalAttachmentId } from '../../../attachments/attachmentIds';
import { http } from '../../../../utils/services/http/http';
import type { AxiosProgressEvent } from 'axios';
import { IMAGE_TYPES } from '../../../../../common/media/mediaConstants';
import { MimeType } from '../../../../../common/media/mimeConstants';
import { TIMES } from '../../../../../common/utils/timeUtil';
import type { MNImageContent } from '../../../../../common/elements/elementModelTypes';
import { enrichBlobAsUploadFile } from '../imageModalFileUtils';
import {
    getBackgroundRemovalMimeType,
    getBackgroundRemovalOutputFormat,
    getOutputFilename,
    type BackgroundRemovalOutputFormat,
} from './imageModalBackgroundRemovalFormatUtils';
import { GenericReduxDispatch, GenericReduxThunkAction } from '../../../../../common/actions/actionTypes';
import {
    BACKGROUND_REMOVAL_PROGRESS_TYPE,
    INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS,
    type BackgroundRemovalProgress,
    type BackgroundRemovalProgressHandler,
} from './imageModalBackgroundRemovalProgressTypes';

export type BackgroundRemovalUploadResult = {
    uploadUrl: string;
    size: MNImageContent;
};

const backgroundRemovalImageUploadThunk = backgroundRemovalImageUpload as (args: {
    id: string;
    file: Blob;
    imageType: string;
    attachmentData: undefined;
    onlyReplaceImage: undefined;
    ignoreFileSizeLimits: boolean;
}) => GenericReduxThunkAction<Promise<BackgroundRemovalUploadResult | undefined>>;

export const dispatchBackgroundRemovalImageUpload = (
    dispatch: GenericReduxDispatch,
    {
        elementId,
        file,
        ignoreFileSizeLimits,
    }: {
        elementId: string;
        file: Blob;
        ignoreFileSizeLimits: boolean;
    },
): Promise<BackgroundRemovalUploadResult | undefined> =>
    dispatch(
        backgroundRemovalImageUploadThunk({
            id: elementId,
            file,
            imageType: IMAGE_TYPES.ELEMENT,
            attachmentData: undefined,
            onlyReplaceImage: undefined,
            ignoreFileSizeLimits,
        }),
    );

const getDeterminateProgress = (
    progressEvent: AxiosProgressEvent,
    fallbackTotal?: number,
): (BackgroundRemovalProgress & { percentageComplete: number }) | null => {
    const total = progressEvent.total || fallbackTotal;
    if (!total) return null;

    return {
        type: BACKGROUND_REMOVAL_PROGRESS_TYPE.DETERMINATE,
        percentageComplete: Math.min((progressEvent.loaded / total) * 100, 100),
    };
};

/**
 * Send an image blob to a server-backed background-removal provider and return the processed transparent image.
 */
const removeBackgroundWithServerModel = async ({
    fileName,
    imageBlob,
    url,
    outputFormat,
    onProgress,
    abortSignal,
}: {
    fileName: string;
    imageBlob: Blob;
    url: string;
    outputFormat: BackgroundRemovalOutputFormat;
    onProgress?: BackgroundRemovalProgressHandler;
    abortSignal?: AbortSignal;
}): Promise<Blob> => {
    onProgress?.(INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS);

    const formData = new FormData();
    formData.append('file', new File([imageBlob], fileName, { type: imageBlob.type || MimeType.PNG }));
    formData.append('format', outputFormat);

    const response = await http<FormData, ArrayBuffer>({
        method: 'post',
        url,
        data: formData,
        timeout: TIMES.MINUTE,
        responseType: 'arraybuffer',
        signal: abortSignal,
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
            const progress = getDeterminateProgress(progressEvent, imageBlob.size);
            onProgress?.(progress || INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS);

            if (progress?.percentageComplete === 100) {
                onProgress?.(INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS);
            }
        },
        onDownloadProgress: (progressEvent) => {
            const progress = getDeterminateProgress(progressEvent);
            onProgress?.(progress || INDETERMINATE_BACKGROUND_REMOVAL_PROGRESS);
        },
    });

    const contentType = response.headers['content-type'];
    return new Blob([response.data], {
        type: typeof contentType === 'string' ? contentType : getBackgroundRemovalMimeType(outputFormat),
    });
};

/**
 * Upload the processed background-removed image and return the image metadata for toggling.
 */
export const uploadProcessedImage = async ({
    dispatch,
    elementId,
    fileName,
    blob,
}: {
    dispatch: GenericReduxDispatch;
    elementId: string;
    fileName: string;
    blob: Blob;
}): Promise<MNImageContent> => {
    const file = enrichBlobAsUploadFile(blob, getOutputFilename(fileName, blob));
    dispatch(resetProgressElementAttachment({ id: getBackgroundRemovalAttachmentId(elementId) }));

    const result = await dispatchBackgroundRemovalImageUpload(dispatch, {
        elementId,
        file,
        ignoreFileSizeLimits: true,
    });

    if (!result) throw new Error('Background-removed image failed to upload');

    return getUploadedImageContent({ ...result, transparent: true }) as MNImageContent;
};

/**
 * Send the current image to the Milanote server, which forwards it to the configured background-removal provider.
 */
export const removeBackground = (
    imageBlob: Blob,
    fileName: string,
    onProgress?: BackgroundRemovalProgressHandler,
    // Aborting cancels the in-flight provider request (e.g. when the user dismisses background mode).
    abortSignal?: AbortSignal,
): Promise<Blob> =>
    removeBackgroundWithServerModel({
        fileName,
        imageBlob,
        url: 'images/remove-background/',
        outputFormat: getBackgroundRemovalOutputFormat(imageBlob),
        onProgress,
        abortSignal,
    });
